// Right-rail Watchers + Activity panels on the ticket detail page.
//
//   1. <TicketWatchersPanel> — avatar chips + the Watch/teammate picker.
//   2. <TicketActivityPanel> — vertical timeline of ticket_events with tinted
//      icon chips, mirroring the notification-row aesthetic from
//      <NotificationsBell>.
//
// Polling lives in the underlying queries (useTicketEvents is fetched on demand;
// useTicketWatchers refetches every 30s + on focus). Realtime is
// intentionally not used — see CLAUDE.md.

import { useMemo, useState } from 'react';
import {
  Activity,
  AlarmClock,
  AlertOctagon,
  Archive,
  ArchiveRestore,
  Ban,
  CheckCircle2,
  Hand,
  Inbox,
  Mail,
  MessageSquare,
  RefreshCw,
  RotateCcw,
  Send,
  Sparkles,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  useTicketEvents,
  useTeamMembers,
  useTicketWatchers,
  type TicketEvent,
  type TeamMember,
} from '@/lib/queries';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityListSkeleton, EmptyState } from '@/components/Skeletons';
import { assigneeName, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/UserAvatar';
import { WatcherPicker } from '@/components/WatcherPicker';
import { RailSection } from '@/components/RailSection';

// Mapping from event_type → (icon, tinted-pill colour classes) — same Linear
// palette as `notificationAccent()`. Anything unknown falls back to slate.
const EVENT_STYLE: Record<
  string,
  { icon: LucideIcon; accent: string }
> = {
  created:           { icon: Inbox,        accent: 'bg-tint-neutral text-tint-neutral-fg ring-1 ring-inset ring-tint-neutral-ring' },
  triaged:           { icon: Sparkles,     accent: 'bg-tint-active text-tint-active-fg ring-1 ring-inset ring-tint-active-ring' },
  claimed:           { icon: Hand,         accent: 'bg-tint-info text-tint-info-fg ring-1 ring-inset ring-tint-info-ring' },
  replied_inbound:   { icon: MessageSquare, accent: 'bg-tint-info text-tint-info-fg ring-1 ring-inset ring-tint-info-ring' },
  replied_outbound:  { icon: Send,         accent: 'bg-tint-success text-tint-success-fg ring-1 ring-inset ring-tint-success-ring' },
  status_changed:    { icon: RefreshCw,    accent: 'bg-tint-neutral text-tint-neutral-fg ring-1 ring-inset ring-tint-neutral-ring' },
  sla_breached:      { icon: AlarmClock,   accent: 'bg-tint-critical text-tint-critical-fg ring-1 ring-inset ring-tint-critical-ring' },
  sla_breach:        { icon: AlarmClock,   accent: 'bg-tint-critical text-tint-critical-fg ring-1 ring-inset ring-tint-critical-ring' },
  follow_up_sent:    { icon: Mail,         accent: 'bg-tint-active text-tint-active-fg ring-1 ring-inset ring-tint-active-ring' },
  escalated:         { icon: AlertOctagon, accent: 'bg-tint-critical text-tint-critical-fg ring-1 ring-inset ring-tint-critical-ring' },
  resolved:          { icon: CheckCircle2, accent: 'bg-tint-success text-tint-success-fg ring-1 ring-inset ring-tint-success-ring' },
  reopened:          { icon: RotateCcw,    accent: 'bg-tint-warning text-tint-warning-fg ring-1 ring-inset ring-tint-warning-ring' },
  error:             { icon: XCircle,      accent: 'bg-tint-critical text-tint-critical-fg ring-1 ring-inset ring-tint-critical-ring' },
};

const FALLBACK_STYLE = {
  icon: RefreshCw as LucideIcon,
  accent: 'bg-tint-neutral text-tint-neutral-fg ring-1 ring-inset ring-tint-neutral-ring',
};

// Events rendered per page. Older ones are still in the DB; the Activity
// panel reveals them this many at a time via "Load earlier events".
//
// Watchers and Activity are separate exports (rather than one component
// rendering both) so the page can put Watchers at the top of the rail —
// adding a watcher shouldn't need a scroll — and leave Activity at the
// bottom. Both render through <RailSection>, which caps the height and
// adds a "Show more" toggle when the content overflows.
const EVENTS_PAGE = 50;

function useTeamMap() {
  const { data: teamMembers } = useTeamMembers();
  return useMemo(() => {
    const map = new Map<string, TeamMember>();
    for (const m of teamMembers ?? []) map.set(m.slack_user_id, m);
    return map;
  }, [teamMembers]);
}

export function TicketWatchersPanel({ ticketId }: { ticketId: string }) {
  const watchersQ = useTicketWatchers(ticketId);
  const teamMembersBySlackId = useTeamMap();
  const watchers = watchersQ.data ?? [];

  return (
    <RailSection
      title="Watchers"
      count={watchers.length}
      // Dropdown picker — lets the signed-in user (a) toggle their own
      // watch with one click (Self row pinned to top) AND (b) add or
      // remove any teammate as a watcher from the same menu. The RLS that
      // gates this widened in migration 041.
      action={
        <WatcherPicker
          ticketId={ticketId}
          watchers={watchers}
          teamMembersBySlackId={teamMembersBySlackId}
        />
      }
      collapsedMaxHeight={5.5}
    >
      {watchersQ.isLoading ? (
        <Skeleton className="h-7 w-32" />
      ) : watchers.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nobody&apos;s watching this ticket yet.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {watchers.map((w) => (
            <UserAvatar
              key={w.slack_user_id}
              slackId={w.slack_user_id}
              teamMembersBySlackId={teamMembersBySlackId}
              size="md"
            />
          ))}
        </div>
      )}
    </RailSection>
  );
}

export function TicketActivityPanel({ ticketId }: { ticketId: string }) {
  const eventsQ = useTicketEvents(ticketId);
  const teamMembersBySlackId = useTeamMap();
  const [shown, setShown] = useState(EVENTS_PAGE);

  // Render most-recent-first in the sidebar (the underlying query orders ASC
  // because the Events tab used a plain log style; here the timeline reads
  // newest at top to match the sidebar idiom).
  const events = useMemo(() => (eventsQ.data ?? []).slice().reverse(), [eventsQ.data]);

  const visible = events.slice(0, shown);
  const hidden = Math.max(0, events.length - visible.length);

  return (
    <RailSection
      title="Activity"
      count={events.length > 0 ? events.length : undefined}
      collapsedMaxHeight={20}
    >
      {eventsQ.isLoading ? (
        <ActivityListSkeleton count={5} />
      ) : events.length === 0 ? (
        <EmptyState
          icon={<Activity className="h-4 w-4" />}
          title="No activity yet"
          subtitle="Replies, status changes, and notes will show up here as the ticket moves."
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {visible.map((ev) => (
            <ActivityRow
              key={ev.id}
              event={ev}
              teamMembersBySlackId={teamMembersBySlackId}
            />
          ))}
          {hidden > 0 ? (
            <li className="pt-1">
              <button
                type="button"
                onClick={() => setShown((n) => n + EVENTS_PAGE)}
                className="text-2xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Load {Math.min(hidden, EVENTS_PAGE)} earlier event
                {Math.min(hidden, EVENTS_PAGE) === 1 ? '' : 's'} ({hidden} not shown)
              </button>
            </li>
          ) : null}
        </ul>
      )}
    </RailSection>
  );
}


interface ActivityRowProps {
  event: TicketEvent;
  teamMembersBySlackId: Map<string, TeamMember>;
}

function ActivityRow({ event, teamMembersBySlackId }: ActivityRowProps) {
  const baseStyle = EVENT_STYLE[event.event_type] ?? FALLBACK_STYLE;

  const actor = friendlyActor(event.actor, teamMembersBySlackId);
  // describeEvent uses this to humanise slack-id values in payloads
  // (e.g. payload.to on a reassign).
  const resolveId = (id: string) => friendlyActor(id, teamMembersBySlackId);
  const { title, subtitle, iconOverride } = describeEvent(event, actor, resolveId);
  const Icon = iconOverride ?? baseStyle.icon;
  const accent = baseStyle.accent;

  return (
    <li className="flex items-start gap-2.5">
      <span
        className={cn(
          'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
          accent,
        )}
      >
        <Icon className="h-3 w-3" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="min-w-0 truncate text-sm leading-tight text-foreground/90">
            {title}
          </p>
          <span className="shrink-0 text-3xs text-muted-foreground">
            {formatDateTime(event.created_at)}
          </span>
        </div>
        {subtitle ? (
          <p className="mt-0.5 truncate text-2xs text-muted-foreground">
            {subtitle}
          </p>
        ) : null}
      </div>
    </li>
  );
}

// Resolve a ticket_event.actor (which may be a slack user id, an email, or
// 'system') to a friendly display name.
function friendlyActor(
  actor: string | null,
  teamMembersBySlackId: Map<string, TeamMember>,
): string {
  if (!actor) return 'someone';
  if (actor === 'system') return 'system';
  if (actor.startsWith('U') || actor.startsWith('W') || actor.startsWith('B')) {
    return assigneeName(actor, teamMembersBySlackId);
  }
  return actor;
}

// Build a `{ title, subtitle }` for a ticket_event, using payload fields when
// they're useful. Falls back to a humanised event_type for unknown types.
function describeEvent(
  ev: TicketEvent,
  actor: string,
  // Resolves a Slack ID (e.g. payload.to on a reassign) to a human name.
  // Falls through unchanged for non-slack-id strings.
  resolveId: (id: string) => string = (s) => s,
): { title: string; subtitle: string | null; iconOverride?: LucideIcon } {
  const payload = (ev.payload ?? {}) as Record<string, unknown>;
  // Actions applied by an automation rule carry { via: 'automation', rule_name }
  // in their payload. Attribute them to the automation (with the rule name)
  // instead of whichever admin happened to trigger the run — otherwise an
  // auto-assign reads as "Rex claimed this ticket".
  if (payload.via === 'automation') {
    const rn = typeof payload.rule_name === 'string' && payload.rule_name ? payload.rule_name : '';
    actor = rn ? `Automation (${rn})` : 'Automation';
  }
  const str = (k: string): string | null => {
    const v = payload[k];
    return typeof v === 'string' && v.length > 0 ? v : null;
  };
  const num = (k: string): number | null => {
    const v = payload[k];
    return typeof v === 'number' ? v : null;
  };

  switch (ev.event_type) {
    case 'created':
      return { title: 'Ticket created', subtitle: null };

    case 'triaged': {
      const priority = str('priority');
      const category = str('category');
      const sub = [priority, category].filter(Boolean).join(' · ');
      return { title: 'Triage complete', subtitle: sub || null };
    }

    case 'claimed': {
      // Reassigns are also recorded as `claimed` events with from/to in the
      // payload (kept the event_type narrow per the migration's CHECK).
      const from = str('from');
      const to = str('to');
      if (from && to && from !== to) {
        return {
          title: `${actor} reassigned`,
          subtitle: `${resolveId(from)} → ${resolveId(to)}`,
        };
      }
      if (to && !from) {
        return {
          title: `${actor} assigned to ${resolveId(to)}`,
          subtitle: null,
        };
      }
      return { title: `${actor} claimed this ticket`, subtitle: null };
    }

    case 'replied_inbound':
      return {
        title: `${actor} replied`,
        subtitle: str('preview') ?? 'Inbound message',
      };

    case 'replied_outbound':
      return {
        title: `${actor} replied`,
        subtitle: str('preview'),
      };

    case 'status_changed': {
      const kind = str('kind');

      // Snooze (new style, migration 037+): { kind: 'snoozed', snoozed_until }.
      // The legacy "event-only" snooze wrote { snoozed_for_hours } instead;
      // we keep the fallback so old events still render nicely.
      if (kind === 'snoozed') {
        const until = str('snoozed_until');
        return {
          title: `${actor} snoozed ticket`,
          subtitle: until
            ? `until ${new Date(until).toLocaleString(undefined, { weekday: 'short', day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' })}`
            : null,
        };
      }
      if (kind === 'snooze_expired') {
        const manual = (ev.payload as Record<string, unknown>)?.manual === true;
        return {
          title: manual ? `${actor} woke ticket up` : 'Snooze expired',
          subtitle: manual ? 'manual wake' : 'auto-resurfaced — back in queue',
        };
      }
      const snoozeHours = num('snoozed_for_hours');
      if (snoozeHours !== null) {
        return {
          title: `${actor} snoozed ticket`,
          subtitle: `for ${snoozeHours}h`,
        };
      }

      // Archive / unarchive / close — dispatcher writes a `kind` discriminator
      // alongside status_changed, since the event_type CHECK constraint is
      // narrow.
      if (kind === 'archived') {
        return {
          title: `${actor} archived ticket`,
          subtitle: 'hidden from dashboard',
          iconOverride: Archive,
        };
      }
      if (kind === 'unarchived') {
        return {
          title: `${actor} unarchived ticket`,
          subtitle: 'restored to dashboard',
          iconOverride: ArchiveRestore,
        };
      }
      if (kind === 'closed') {
        return {
          title: `${actor} closed ticket`,
          subtitle: 'no action needed (marketing / spam / duplicate)',
          iconOverride: Ban,
        };
      }
      if (kind === 'priority_set') {
        const toP = str('to_priority');
        const fromP = str('from_priority');
        return {
          title: `${actor} set priority${toP ? ` to ${toP}` : ''}`,
          subtitle: fromP && toP ? `${fromP} → ${toP}` : null,
        };
      }

      // Merge — source side: { kind: 'merge_source', merged_into_subject }
      const mergedIntoSubject = str('merged_into_subject');
      if (mergedIntoSubject || str('merged_into_ticket_id')) {
        return {
          title: `${actor} merged ticket out`,
          subtitle: mergedIntoSubject ? `into "${mergedIntoSubject}"` : 'into another ticket',
        };
      }

      // Merge — target side: { kind: 'merge_target', merged_from_subject }
      const mergedFromSubject = str('merged_from_subject');
      if (mergedFromSubject || str('merged_from_ticket_id')) {
        return {
          title: `Received merged thread`,
          subtitle: mergedFromSubject ? `from "${mergedFromSubject}"` : 'from another ticket',
        };
      }

      // Generic from/to fallback for any future writers that include them.
      const from = str('from_status') ?? str('from');
      const to = str('to_status') ?? str('to');
      if (from && to) {
        return {
          title: `${actor} changed status`,
          subtitle: `${from.replace(/_/g, ' ')} → ${to.replace(/_/g, ' ')}`,
        };
      }
      if (to) {
        return {
          title: `${actor} changed status to ${to.replace(/_/g, ' ')}`,
          subtitle: null,
        };
      }

      return { title: `${actor} changed status`, subtitle: null };
    }

    case 'sla_breached':
    case 'sla_breach': {
      const t = str('type') ?? str('sla_type');
      const overdue = num('overdue_minutes');
      return {
        title: t ? `SLA breach · ${t}` : 'SLA breach',
        subtitle: overdue !== null ? `${overdue} min overdue` : null,
      };
    }

    case 'follow_up_sent':
      return {
        title: `Follow-up sent by ${actor}`,
        subtitle: str('preview'),
      };

    case 'escalated': {
      const from = str('from_priority');
      const to = str('to_priority');
      const sub = from && to ? `${from} → ${to}` : null;
      return {
        title: sub ? `${actor} escalated · ${sub}` : `${actor} escalated`,
        subtitle: null,
      };
    }

    case 'resolved':
      return { title: `${actor} resolved this ticket`, subtitle: null };

    case 'reopened':
      return { title: `${actor} reopened this ticket`, subtitle: null };

    case 'error':
      return {
        title: 'Error',
        subtitle: str('message') ?? str('ctx') ?? null,
      };

    default:
      return {
        title: `${ev.event_type.replace(/_/g, ' ')} by ${actor}`,
        subtitle: null,
      };
  }
}
